import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Form } from "react-bootstrap";
import { useAlert } from "react-alert";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/mousewheel";
import { Mousewheel } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { doc, runTransaction } from "firebase/firestore";
import getIcon from "../../utility/getIcon";
import OutlinedPinkButton from "../../components/buttons/outlinedPinkButton/OutlinedPinkButton";
import {
  vatValidation,
  priceValidation,
  normalizeOptionalNumber,
} from "../../helper/regexValidations";

import PictureDisplaySelector from "./pictureDisplaySelector/PictureDisplaySelector";
import { db } from "../../firebase";
import { uploadMedia } from "../../utility/uploadMedia";
import deleteImage from "../../utility/deleteImage";
import Input from "../Input";
import LoadingSpinner from "../../components/LoadingSpinner";
import { ReactComponent as AngleLeft } from "../../images/AngleLeft.svg";
import { ReactComponent as Beer } from "../../images/Beers.svg";
import { ReactComponent as Softs } from "../../images/Softs.svg";
import { ReactComponent as Food } from "../../images/Fast-food.svg";
import { ReactComponent as HotDrinks } from "../../images/hotDrinks.svg";
import { ReactComponent as Cocktails } from "../../images/Cocktails.svg";
import { ReactComponent as Wine } from "../../images/Wine.svg";
import { ReactComponent as Others } from "../../images/Others.svg";
import { CategoriesArray } from "../../utility/data";
import "./AddProduct.scss";
import PosIconModal from "./PosIconModal";
import FormField from "../formField/FormField";
import getBartracksLines from "../../utility/getBartracksLines";
import { getStockItems, getStockCategories } from "../../utility/getStock";
import StockConfigFields from "./StockConfigFields";
import { generateProductIcon, previewProductIcon } from "../../utility/aiApi";
import { normalizeStockLinks } from "../../utility/stockManagement";

const AddProduct = ({
  org,
  selectedProduct,
  setAddNew,
  customCategories,
  optionCategories = [],
  handleDelete,
}) => {
  const { t } = useTranslation("editproduct");
  const userStore = useSelector((state) => state.userReducer);
  const orgId = org?.id;

  const [selectedCategory, setSelectedCategory] = useState("Beers");
  const [secondaryColor, setSecondaryColor] = useState(false);
  const [uploadIcon, setUploadIcon] = useState(icons[0]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [loader, setLoader] = useState(false);
  const [outOfStock, setOutOfStock] = useState(false);
  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [vat, setVat] = useState("");
  const [vatOut, setVatOut] = useState("");
  const [positifNumber, setPositifNumber] = useState(true);
  const [selectedColor, setSelectedColor] = useState("");
  const [numberOfTicket, setNumberOfTicket] = useState(0);
  const [selectedSecondColor, setSelectedSecondColor] = useState("");
  const [numberOfSecondTicket, setNumberOfSecondTicket] = useState(0);
  const [customIcon, setCustomIcon] = useState("");
  const [barTrackLinesIds, setBarTrackLinesIds] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [stockCategories, setStockCategories] = useState([]);
  const [lines, setLines] = useState([]);
  const [servingSize, setServingSize] = useState();
  const [stockItemId, setStockItemId] = useState("");
  const [quantityPerSale, setQuantityPerSale] = useState("");
  const [stockLinks, setStockLinks] = useState([]);
  const [outOfStockBehavior, setOutOfStockBehavior] = useState("");

  const toggleOptionCategory = (id) => {
    setSelectedOptionCategories((prev) =>
      prev.includes(id) ? prev.filter((categoryId) => categoryId !== id) : [...prev, id],
    );
  };
  const [selectedOptionCategories, setSelectedOptionCategories] = useState([]);
  const [imageUrl, setImageUrl] = useState();
  const [generatingAiIcon, setGeneratingAiIcon] = useState(false);

  function handlePictureChange(event) {
    const file = event.target.files[0];

    if (file) {
      if (file.type.startsWith("image/")) {
        const url = URL.createObjectURL(file);
        setImageUrl(url);
      } else {
        alert.show(t("fileNotImage"));
      }
    }
  }

  const fetchBarTracksLines = useCallback(
    async (signal) => {
      try {
        const lines = await getBartracksLines(org?.id);
        if (!signal.aborted) setLines(lines);
      } catch (error) {
        console.error(error);
      }
    },
    [org?.id],
  );

  const fetchStockItems = useCallback(
    async (signal) => {
      try {
        const stockItems = await getStockItems(org?.uid);
        if (!signal.aborted) setStockItems(stockItems);
      } catch (error) {
        console.error(error);
        alert.show(t("stock_items_load_error"), { type: "error" });
      }
    },
    [org?.uid],
  );

  const fetchStockCategories = useCallback(
    async (signal) => {
      try {
        const categories = await getStockCategories(org?.uid);
        if (!signal.aborted) setStockCategories(categories);
      } catch (error) {
        console.error(error);
        alert.show(t("stock_categories_load_error"), { type: "error" });
      }
    },
    [org?.uid],
  );

  useEffect(() => {
    const abortController = new AbortController();

    const primaryTicketNumber = Number(selectedProduct?.ticketNumber ?? 0);
    const secondaryTicketNumber = Number(selectedProduct?.secondTicketNumber ?? 0);
    const referenceTicketNumber = primaryTicketNumber !== 0 ? primaryTicketNumber : secondaryTicketNumber;

    setSelectedCategory(selectedProduct?.category || "Beers");
    setSecondaryColor(Boolean(selectedProduct?.secondTicketColor));
    setUploadIcon(selectedProduct ? selectedProduct.customIcon : icons[0]); // Assuming icons[0] is a default icon
    setOutOfStock(selectedProduct?.outOfStock || false);
    setTitle(selectedProduct?.title || "");
    setPrice(selectedProduct?.price ?? "");
    setVat(selectedProduct?.vat ?? "");
    setVatOut(selectedProduct?.vatOut ?? "");
    setPositifNumber(referenceTicketNumber >= 0);
    setSelectedColor(selectedProduct?.ticketColor || "");
    setNumberOfTicket(Math.abs(primaryTicketNumber));
    setSelectedSecondColor(selectedProduct?.secondTicketColor || "");
    setNumberOfSecondTicket(Math.abs(secondaryTicketNumber));
    setCustomIcon(selectedProduct?.customIcon || "");
    setSelectedOptionCategories(selectedProduct?.optionCategories || []);
    setImageUrl(selectedProduct?.imageUrl || null);
    setBarTrackLinesIds(selectedProduct?.linesIds || []);
    setServingSize(selectedProduct?.servingSize || null);
    const initialStockLinks = normalizeStockLinks(selectedProduct).map((link) => ({
      stockItemId: link.stockItemId,
      quantityPerSale: `${link.quantityPerSale}`,
    }));
    setStockLinks(initialStockLinks);
    setStockItemId(initialStockLinks[0]?.stockItemId || "");
    if (initialStockLinks.length > 0) {
      setQuantityPerSale(initialStockLinks[0]?.quantityPerSale || "");
      setOutOfStockBehavior(selectedProduct?.outOfStockBehavior || "alert");
    } else {
      setQuantityPerSale("");
      setOutOfStockBehavior("");
    }

    fetchStockItems(abortController.signal);
    fetchStockCategories(abortController.signal);
    if (org?.bartrackUUID) fetchBarTracksLines(abortController.signal);

    return () => abortController.abort();
  }, [
    selectedProduct,
    optionCategories,
    fetchStockItems,
    fetchStockCategories,
    fetchBarTracksLines,
  ]);

  const alert = useAlert();

  const saveProduct = async () => {
    const sanitizedStockLinks = stockLinks
      .map((link) => ({
        stockItemId: link?.stockItemId || "",
        quantityPerSale: Number(`${link?.quantityPerSale || ""}`.replace(/,/g, ".")),
      }))
      .filter((link) => link.stockItemId || link.quantityPerSale);
    const invalidStockLink = sanitizedStockLinks.find(
      (link) =>
        !link.stockItemId || !Number.isFinite(link.quantityPerSale) || link.quantityPerSale <= 0,
    );
    const validStockLinks = sanitizedStockLinks.filter(
      (link) => link.stockItemId && Number.isFinite(link.quantityPerSale) && link.quantityPerSale > 0,
    );
    const primaryStockLink = validStockLinks[0];

    if (title === "") {
      alert.show(t("choose_name"), { type: "warning" });
    } else if (price === "") {
      alert.show(t("price_required"), { type: "warning" });
    } else if (!priceValidation(price)) {
      alert.show(t("price_invalid_format"), { type: "warning" });
    } else if (!vatValidation(vat) || !vatValidation(vatOut)) {
      alert.show(t("vat_range"), { type: "warning" });
    } else if (selectedCategory === null) {
      alert.show("Choisissez une catégorie!", { type: "warning" });
    } else if (invalidStockLink) {
      alert.show(t("quantity_per_sale_required"), { type: "warning" });
    } else {
      setLoader(true);
      const id = selectedProduct?.id ? selectedProduct.id : new Date().getTime().toString();
      const productData = {
        title,
        price: normalizeOptionalNumber(price),
        vat: normalizeOptionalNumber(vat),
        vatOut: normalizeOptionalNumber(vatOut),
        customIcon,
        category: selectedCategory,
        id,
        ticketColor: selectedColor,
        ticketNumber: positifNumber ? Math.abs(numberOfTicket) : -Math.abs(numberOfTicket),
        secondTicketColor: selectedSecondColor,
        secondTicketNumber: positifNumber
          ? Math.abs(numberOfSecondTicket)
          : -Math.abs(numberOfSecondTicket),
        uid: userStore?.users.uid,
        orgId,
        outOfStock,
        optionCategories: selectedOptionCategories,
        imageUrl: imageUrl || null,
        linesIds: barTrackLinesIds || null,
        servingSize: servingSize || null,
        stockLinks: validStockLinks,
        stockItemId: primaryStockLink?.stockItemId || null,
        quantityPerSale: primaryStockLink?.quantityPerSale || null,
        outOfStockBehavior: validStockLinks.length > 0 ? outOfStockBehavior || "alert" : null,
      };

      let oldImageUrl = selectedProduct?.imageUrl || null;
      let uploadedImageUrl = null;

      try {
        if (imageUrl && (!selectedProduct || imageUrl !== oldImageUrl)) {
          if (oldImageUrl) {
            await deleteImage(oldImageUrl);
          }
          uploadedImageUrl = await uploadMedia(imageUrl, `products/${id}`, false);
          productData.imageUrl = uploadedImageUrl;
        }

        await runTransaction(db, async (transaction) => {
          const productRef = doc(db, "products", id);
          transaction.set(productRef, productData, { merge: true });
        });

        setLoader(false);
        setAddNew(false);
        alert.show("Produit enregistré avec succès.", { type: "success" });
      } catch (error) {
        console.log("Error writing document: ", error);
        setLoader(false);
        alert.show("Quelque chose n'a pas marché.", { type: "error" });
        if (uploadedImageUrl) {
          await deleteImage(uploadedImageUrl);
        }
      }
    }
  };

  const handleSelectCategory = (ctg) => {
    setSelectedCategory(ctg);
    setCustomIcon("");
    setVat("");
    setVatOut("");
  };
  const handleSelectCustomCategory = (ctg) => {
    setSelectedCategory(ctg.title);
    setCustomIcon(ctg.customIcon);
    setVat(ctg?.vatIn);
    setVatOut(ctg?.vatOut);
  };

  const handleNewLine = (lineId) => {
    if (lineId) {
      setBarTrackLinesIds((prev) => (!prev.includes(lineId) ? [...prev, lineId] : prev));
    }
  };

  const handleRemoveLine = (index) => {
    const lines = barTrackLinesIds.filter((_, i) => i !== index);
    setBarTrackLinesIds(lines);

    if (lines.length === 0) {
      setServingSize(null);
    }
  };

  const handleGenerateAiIcon = async () => {
    if (!title?.trim() || !orgId || generatingAiIcon) return;
    setGeneratingAiIcon(true);
    try {
      if (selectedProduct?.id) {
        await generateProductIcon({
          orgId,
          productId: selectedProduct.id,
          collectionName: "products",
        });
      } else {
        const result = await previewProductIcon({
          orgId,
          title: title.trim(),
          category: selectedCategory || "Other",
        });
        setImageUrl(result.imageUrl);
      }
      alert.show(t("ai_icon_generation_started"), { type: "success" });
    } catch (error) {
      alert.show(error.message || t("ai_icon_generation_error"), { type: "error" });
    } finally {
      setGeneratingAiIcon(false);
    }
  };

  return (
    <div className="add-product">
      <h4 className="add-product-title" onClick={() => setAddNew(false)}>
        <AngleLeft /> <div className="w-0 flex-grow-1">{t("return")}</div>
      </h4>
      <div className="item">
        <h5 className="subtitle">{t("the_product_is")}</h5>
        <div className="mb-28 gap-12px d-flex">
          <button
            type="button"
            className={`themeBtn themeBtnSecondary m-0 w-100 pos-submit-btn ${
              outOfStock ? "btn-outline-success" : "btn-success active"
            }`}
            onClick={() => setOutOfStock(false)}
          >
            {t("in_stock")}
          </button>
          <button
            type="button"
            className={`themeBtn themeBtnSecondary m-0 w-100 pos-submit-btn ${
              outOfStock ? "btn-danger active" : "btn-outline-danger"
            }`}
            onClick={() => setOutOfStock(true)}
          >
            {t("out_of_stock")}
          </button>
        </div>
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="mb-20">
            <Input
              label={t("title")}
              placeholder={t("product_name")}
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required="required"
            />
          </div>
          <div className="mb-20">
            <Input
              label={t("price")}
              placeholder="5.99"
              id="price"
              type="text"
              value={price}
              onChange={(e) => {
                if (priceValidation(e.target.value)) setPrice(e.target.value);
              }}
              required="required"
            />
          </div>
          <div className="mb-20">
            <label className="form-label __form-label">{t("category")}</label>
            <div className="filterRow">
              <div
                className={selectedCategory === "Beers" ? "filBtn active" : "filBtn"}
                onClick={() => handleSelectCategory("Beers")}
              >
                <Beer />
                <span>{t("beers")}</span>
              </div>
              <div
                className={selectedCategory === "Softs" ? "filBtn active" : "filBtn"}
                onClick={() => handleSelectCategory("Softs")}
              >
                <Softs />
                <span>{t("softs")}</span>
              </div>
              <div
                className={selectedCategory === "Food" ? "filBtn active" : "filBtn"}
                onClick={() => handleSelectCategory("Food")}
              >
                <Food />
                <span>{t("food")}</span>
              </div>
              <div
                className={selectedCategory === "Cocktails" ? "filBtn active" : "filBtn"}
                onClick={() => handleSelectCategory("Cocktails")}
              >
                <Cocktails />
                <span>{t("cocktails")}</span>
              </div>
              <div
                className={selectedCategory === "Wine" ? "filBtn active" : "filBtn"}
                onClick={() => handleSelectCategory("Wine")}
              >
                <Wine />
                <span>{t("wine")}</span>
              </div>
              <div
                className={selectedCategory === "Hot drinks" ? "filBtn active" : "filBtn"}
                onClick={() => handleSelectCategory("Hot drinks")}
              >
                <HotDrinks />
                <span>{t("hot_drinks")}</span>
              </div>
              <div
                className={selectedCategory === "Other" ? "filBtn active" : "filBtn"}
                onClick={() => handleSelectCategory("Other")}
              >
                <Others />
                <span>{t("others")}</span>
              </div>
              {customCategories.map((cat) => (
                <div
                  key={cat.id || cat.title}
                  className={selectedCategory === cat.title ? "filBtn active" : "filBtn"}
                  onClick={() => handleSelectCustomCategory(cat)}
                  style={{ position: "relative", paddingRight: 28 }} // space for the X button
                >
                  <img
                    src={CategoriesArray.find((i) => i.name === cat.customIcon)?.icon}
                    alt={cat?.customIcon}
                    style={{
                      width: 20,
                      height: 20,
                      filter: selectedCategory === cat.title ? "invert(0) brightness(2)" : "none",
                      transition: "filter 0.3s ease",
                      marginRight: 6,
                    }}
                  />
                  <span>{cat.title}</span>
                </div>
              ))}
            </div>
          </div>
          {optionCategories.length > 0 && (
            <div className="mb-20">
              <label className="form-label __form-label">{t("option_categories")}</label>
              <div className="filterRow option-category-filter">
                {optionCategories.map((cat) => (
                  <div
                    key={cat.id}
                    className={
                      selectedOptionCategories.includes(cat.id) ? "filBtn active" : "filBtn"
                    }
                    onClick={() => toggleOptionCategory(cat.id)}
                  >
                    <img
                      src={CategoriesArray.find((i) => i.name === cat.customIcon)?.icon}
                      alt={cat.customIcon}
                      style={{ width: 20, height: 20, marginRight: 6 }}
                    />
                    <span>{cat.title}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="mb-20">
            <Input
              label={t("optional") + " " + t("vat_in")}
              placeholder="6"
              id="vat"
              type="text"
              value={vat}
              onChange={(e) => {
                if (vatValidation(e.target.value)) setVat(e.target.value);
              }}
            />
          </div>
          <div className="mb-20">
            <Input
              label={t("optional") + " " + t("vat_out")}
              placeholder="21"
              id="vatOut"
              type="text"
              value={vatOut}
              onChange={(e) => {
                if (vatValidation(e.target.value)) setVatOut(e.target.value);
              }}
            />
          </div>
          {lines?.length > 0 && (
            <div className="mb-20">
              <div className="mb-20">
                <FormField
                  value={""}
                  onChange={handleNewLine}
                  dropDownValues={[
                    { value: "", label: t("add_line") },
                    ...lines
                      .filter((line) => !barTrackLinesIds.includes(line.id))
                      .map((line) => ({
                        value: line.id,
                        label: t("line_label", { name: line?.name }),
                      })),
                  ]}
                  type="dropdown"
                  label={t("add_drink_to_line")}
                />
              </div>

              <div className="linesIdsContainer mb-20">
                {barTrackLinesIds?.map((lineId, index) => {
                  const currentLine = lines.find((l) => lineId === l.id);

                  return (
                    <div key={currentLine.id} className="lineIdItem mb-10">
                      <p className="lineIdName">{t("line_label", { name: currentLine?.name })}</p>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => handleRemoveLine(index)}
                      >
                        {t("remove")}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {barTrackLinesIds.length > 0 && (
            <div className="sizeChoiceContainer mb-20">
              <Input
                label={t("serving_size_bartrack")}
                value={servingSize}
                placeholder="ex: 25, 33, 50, 75, 150"
                onChange={(e) => setServingSize(e.target.value)}
                type="number"
              />
            </div>
          )}

          <StockConfigFields
            stockItemId={stockItemId}
            stockLinks={stockLinks}
            stockItems={stockItems}
            stockCategories={stockCategories}
            quantityPerSale={quantityPerSale}
            outOfStockBehavior={outOfStockBehavior}
            onStockLinksChange={setStockLinks}
            onStockIdChange={setStockItemId}
            onQuantityChange={setQuantityPerSale}
            onOutOfStockBehaviorChange={setOutOfStockBehavior}
          />

          <TokenColor
            label={t("color_token")}
            positifNumber={positifNumber}
            setPositifNumber={setPositifNumber}
            numberOfTicket={numberOfTicket}
            setNumberOfTicket={setNumberOfTicket}
            selectedColor={selectedColor}
            setSelectedColor={setSelectedColor}
          />
          <div className="py-4 d-flex justify-content-between align-items-center border-y mb-4">
            <h5 className="subtitle m-0">{t("second_color")}</h5>
            <Form.Check
              type="switch"
              checked={secondaryColor}
              onChange={(e) => setSecondaryColor(e.target.checked)}
            />
          </div>
          {secondaryColor && (
            <TokenColor
              label={t("choose_second_color")}
              positifNumber={positifNumber}
              setPositifNumber={setPositifNumber}
              numberOfTicket={numberOfSecondTicket}
              setNumberOfTicket={setNumberOfSecondTicket}
              selectedColor={selectedSecondColor}
              setSelectedColor={setSelectedSecondColor}
            />
          )}
          <label className="form-label __form-label">{t("choose_icon") + t("optional")}</label>
          <div className="mb-4">
            <div
              className="productCard max-w-200 mx-auto cursor-pointer"
              onClick={() => setEditModalOpen(true)}
            >
              <span className="color"></span>
              <div className="productImg">
                <img src={getIcon(selectedCategory, customIcon)} alt="" />
              </div>
            </div>
          </div>
          <div className="mb-4">
            <label className="form-label __form-label">{t("choose_image") + t("optional")}</label>
            <PictureDisplaySelector
              onImageChange={(event) => handlePictureChange(event)}
              imageUrl={imageUrl}
              error={null}
              onGenerateAiIcon={handleGenerateAiIcon}
              aiIconLoading={generatingAiIcon}
              aiIconDisabled={!title?.trim()}
              aiIconStatus={
                selectedProduct?.aiIconStatus === "pending"
                  ? t("ai_icon_pending")
                  : selectedProduct?.aiIconStatus === "failed"
                    ? t("ai_icon_failed")
                    : ""
              }
            />
          </div>
          {loader ? (
            <LoadingSpinner height="30%" width="30%" />
          ) : (
            <div className="buttonsWrapper">
              <button
                type="submit"
                className="themeBtn m-0 w-100 pos-submit-btn"
                onClick={saveProduct}
              >
                {t("save")}
              </button>
              {!!selectedProduct && (
                <OutlinedPinkButton onPressFunction={handleDelete}>
                  {t("delete")}
                </OutlinedPinkButton>
              )}
            </div>
          )}
        </form>
        <PosIconModal
          modalOpen={editModalOpen}
          setModalOpen={setEditModalOpen}
          customIcon={customIcon}
          setCustomIcon={setCustomIcon}
          selectedCategory={selectedCategory}
        />
      </div>
    </div>
  );
};

const TokenColor = ({
  label,
  positifNumber,
  setPositifNumber,
  numberOfTicket,
  setNumberOfTicket,
  selectedColor,
  setSelectedColor,
}) => {
  const { t } = useTranslation("editproduct");
  const handleColorClick = (color) => {
    if (color === selectedColor) {
      setNumberOfTicket(numberOfTicket + 1);
    } else {
      setSelectedColor(color);
      setNumberOfTicket(1);
    }
  };
  return (
    <>
      {label && <label className="form-label __form-label">{label}</label>}
      <div className="mb-20 gap-12px d-flex">
        <div
          className="themeBtn themeBtnSecondary m-0 w-100 pos-submit-btn btn-dark token-count-display"
          aria-live="polite"
        >
          <span>{`${t("number")}: ${positifNumber ? "+" : "-"}${numberOfTicket}`}</span>
          {numberOfTicket !== 0 && (
            <button
              type="button"
              className="token-count-reset"
              onClick={() => setNumberOfTicket(0)}
              aria-label={t("reset_token_count")}
              title={t("reset_token_count")}
            >
              ×
            </button>
          )}
        </div>
        <button
          type="button"
          className={`themeBtn themeBtnSecondary m-0 w-100 pos-submit-btn ${
            positifNumber ? "btn-success" : "btn-negative"
          }`}
          onClick={() => setPositifNumber(!positifNumber)}
        >
          {positifNumber ? t("positive") : t("negative")}
        </button>
      </div>
      <div className="colors-wrapper overflow-hidden">
        <Swiper
          slidesPerView="auto"
          modules={[Mousewheel]}
          spaceBetween={10}
          mousewheel={{
            invert: false,
            forceToAxis: true,
          }}
          direction="horizontal"
        >
          {colors.map((item, index) => (
            <SwiperSlide key={index}>
              <div
                className="color"
                style={{ backgroundColor: item.color }}
                onClick={() => handleColorClick(item.color)}
              >
                {numberOfTicket > 0 && item.color === selectedColor && (
                  <span className="counter">{numberOfTicket}</span>
                )}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </>
  );
};

const icons = [
  {
    img: "/img/products/4.png",
    id: "1",
  },
  {
    img: "/img/products/1.png",
    id: "2",
  },
  {
    img: "/img/products/2.png",
    id: "3",
  },
  {
    img: "/img/products/3.png",
    id: "4",
  },
  {
    img: "/img/products/1.png",
    id: "5",
  },
  {
    img: "/img/products/4.png",
    id: "6",
  },
  {
    img: "/img/products/1.png",
    id: "7",
  },
  {
    img: "/img/products/2.png",
    id: "8",
  },
  {
    img: "/img/products/3.png",
    id: "9",
  },
  {
    img: "/img/products/1.png",
    id: "10",
  },
];
const colors = [
  {
    color: "#ed0000",
    id: "1",
  },
  {
    color: "#F44336",
    id: "2",
  },
  {
    color: "#E91E63",
    id: "3",
  },
  {
    color: "#9C27B0",
    id: "4",
  },
  {
    color: "#673AB7",
    id: "5",
  },
  {
    color: "#3F51B5",
    id: "6",
  },
  {
    color: "#2196F3",
    id: "7",
  },
  {
    color: "#03A9F4",
    id: "8",
  },
  {
    color: "#00BCD4",
    id: "9",
  },
  {
    color: "#009688",
    id: "10",
  },
  {
    color: "#4CAF50",
    id: "11",
  },
  {
    color: "#8BC34A",
    id: "12",
  },
  {
    color: "#CDDC39",
    id: "13",
  },
  {
    color: "#FFEB3B",
    id: "14",
  },
  {
    color: "#FFC107",
    id: "15",
  },
  {
    color: "#FF9800",
    id: "16",
  },
  {
    color: "#FF5722",
    id: "17",
  },
  {
    color: "#795548",
    id: "18",
  },
  {
    color: "#9E9E9E",
    id: "19",
  },
  {
    color: "#607D8B",
    id: "20",
  },
  {
    color: "#000000",
    id: "21",
  },
];

export default AddProduct;
